import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Chip, InputAdornment, Modal, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ReusableTable from '../../common/table/CommonTable';
import {
  deleteSecurityOfficer,
  fetchSecurityOfficers,
} from '../../redux/secuityOfficer/securityAction';

const SecurityGuardList = () => {
  const [loading, setLoading] = useState(false);
  const [openDeleteModel, setopenDeleteModel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteId, setdeleteId] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const securityOfficerState = useSelector(
    (state) => state?.securityOfficer?.fetchSecurityOfficers || {}
  );
  const deleteSecurityOfficerState = useSelector(
    (state) => state.securityOfficer.deleteSecurityOfficer || {}
  );
  
  const securityOfficerList = securityOfficerState?.data?.data || [];
  const totalPages = securityOfficerState?.securityOfficer?.totalPages || 0;
  
  // Fetch data on initial load and page change
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await dispatch(fetchSecurityOfficers(currentPage));
      setLoading(false);
    };
    
    loadData();
  }, [currentPage, dispatch]);

  // Refetch data after successful delete
  useEffect(() => {
    if (deleteSecurityOfficerState.success) {
      dispatch(fetchSecurityOfficers(currentPage));
    }
  }, [deleteSecurityOfficerState.success, dispatch, currentPage]);

  // Process and filter data using useMemo to prevent rendering loops
  const filteredData = useMemo(() => {
    // First process the data
    const processedData = securityOfficerList.map((officer) => ({
      ...officer,
      blockName: officer.Block?.block_name || 'N/A',
      floorName: officer.Floor?.floor_name || 'N/A',
      wardName: officer.Ward?.ward_name || 'N/A',
    }));
    
    // Then filter based on search term
    if (!searchTerm.trim()) {
      return processedData;
    }
    
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return processedData.filter((officer) => 
      String(officer.security_serial_no || '').toLowerCase().includes(lowercasedSearchTerm) ||
      String(officer.security_gaurd_name || '').toLowerCase().includes(lowercasedSearchTerm) ||
      String(officer.email || '').toLowerCase().includes(lowercasedSearchTerm) ||
      String(officer.phone_number || '').toLowerCase().includes(lowercasedSearchTerm) ||
      String(officer.blockName || '').toLowerCase().includes(lowercasedSearchTerm) ||
      String(officer.floorName || '').toLowerCase().includes(lowercasedSearchTerm) ||
      String(officer.wardName || '').toLowerCase().includes(lowercasedSearchTerm)
    );
  }, [securityOfficerList, searchTerm]);

  const columns = [
    {
      id: "security_serial_no",
      label: "Guard ID",
      sortable: true,
      render: (value, row) => (
        <Typography
          component="span"
          sx={{
            cursor: "pointer",
            color: "dodgerblue",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() => handleViewClick(row)}
        >
          {value}
        </Typography>
      ),
    },
    // { id: 'security_serial_no', label: 'Guard ID', sortable: true },
    { id: 'security_gaurd_name', label: 'Name', sortable: true },
    { id: 'email', label: 'Email', sortable: true },
    { id: 'phone_number', label: 'Phone Number', sortable: true },
    { id: 'blockName', label: 'Block', sortable: true },
    { id: 'floorName', label: 'Floor', sortable: true },
    { id: 'wardName', label: 'Ward', sortable: true },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <Chip
          label={value === 1 ? 'Active' : 'Inactive'}
          color={value === 1 ? 'success' : 'warning'}
        />
      ),
    },
  ];

  const handleViewClick = (row) => {
    navigate(`/securityOfficer/view/${row.id}`);
  };

  const handleEditClick = (row) => {
    navigate(`/securityOfficer/edit/${row.id}`);
  };

  const handleDeleteClick = (row) => {
    setdeleteId(row.id);
    setopenDeleteModel(true);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteConfirm = () => {
    dispatch(deleteSecurityOfficer(deleteId));
    setopenDeleteModel(false);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <Box>
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by name, email, ID, phone number, block, floor or ward..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <ReusableTable
        title="Security Officers"
        columns={columns}
        data={filteredData || []}
        isLoading={loading}
        headerColor="#000"
        rowColor="#ffffff"
        alternateRowColor="#f5f5f5"
        onViewClick={handleViewClick}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        withPagination={true}
        withActions={true}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showDownloadOption={true}
      />
      
      <Modal
        open={openDeleteModel}
        style={{ left: '35%', top: '35%' }}
        onClose={() => {
          setopenDeleteModel(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ width: '500px', background: ' #fff', padding: '20px' }}>
          <Typography
            id="modal-modal-title"
            variant="h5"
            sx={{ fontSize: 'large', fontWeight: '500' }}
            component="h2"
          >
            Delete Security Guard
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            Are you sure you want to delete this security guard?
          </Typography>
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}
          >
            <Button variant="contained" onClick={handleDeleteConfirm}>
              OK
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setopenDeleteModel(false);
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default SecurityGuardList;